/** Field "Freelance" map (spec §9 FREE-001…003): my papers as pins, eligibility evaluated on the device. */
import { useEffect, useMemo, useState } from 'react';
import { addressKey, PIN_COLORS, PIN_LABELS, pinState, suggestStopOrder } from '@civilos/domain';
import { useRouter } from '../shared/router.tsx';
import { PinLegend, SlippyMap } from '../shared/SlippyMap.tsx';
import { Badge, Empty, Notice } from '../shared/ui.tsx';

interface Row {
  p: any;
  ev: any;
}

function pinOf(p: any): { lat: number; lng: number } | null {
  const a = p.address;
  if (a.pin_lat != null && a.pin_lng != null) return { lat: a.pin_lat, lng: a.pin_lng };
  if (a.lat != null && a.lng != null) return { lat: a.lat, lng: a.lng };
  return null;
}

export function navigateUrl(p: any): string {
  const pin = pinOf(p);
  const addr = [p.address.line1, p.address.city, p.address.state, p.address.zip].filter(Boolean).join(', ');
  return pin ? `https://www.google.com/maps/dir/?api=1&destination=${pin.lat},${pin.lng}` : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}`;
}

function useWatchPosition(enabled: boolean) {
  const [pos, setPos] = useState<{ lat: number; lng: number; accuracy: number } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    if (!enabled) return;
    if (!('geolocation' in navigator)) {
      setErr('Location is not available on this device');
      return;
    }
    const id = navigator.geolocation.watchPosition(
      (x) => (setPos({ lat: x.coords.latitude, lng: x.coords.longitude, accuracy: Math.round(x.coords.accuracy) }), setErr(null)),
      (e) => setErr(e.message || 'Could not get your location'),
      { enableHighAccuracy: true, maximumAge: 30_000, timeout: 20_000 },
    );
    return () => navigator.geolocation.clearWatch(id);
  }, [enabled]);
  return { pos, err };
}

export function FieldMap({ rows, online, initial }: { rows: Row[]; online: boolean; initial?: string | null }) {
  const { navigate } = useRouter();
  const [locate, setLocate] = useState(true);
  const [route, setRoute] = useState(true);
  const [sel, setSel] = useState<string | null>(initial ?? null);
  const { pos, err } = useWatchPosition(locate);

  const open = rows.filter((r) => ['ASSIGNED', 'IN_SERVICE'].includes(r.p.status));
  const items = open.map((r) => {
    const eligibleNow = !!r.ev?.attemptNow?.allowed;
    return { ...r, pin: pinOf(r.p), eligibleNow, state: pinState({ eligibleNow, likelihood: null }) };
  });
  const located = items.filter((x) => x.pin);
  const missing = items.filter((x) => !x.pin);

  // Re-plan only when the set of papers changes or the position moves ~0.1 mi, so stop numbers don't jump around.
  const posKey = pos ? `${pos.lat.toFixed(3)},${pos.lng.toFixed(3)}` : '';
  const planKey = located.map((x) => `${x.p.id}:${x.eligibleNow}`).join('|');
  const order = useMemo(() => {
    if (!route) return null;
    const stops = located
      .filter((x) => x.eligibleNow)
      .map((x) => ({ id: x.p.id, lat: x.pin!.lat, lng: x.pin!.lng, addressKey: addressKey(x.p.address), priority: x.ev?.urgency?.tier === 'supervisor_urgent' ? 2 : x.ev?.urgency?.tier === 'late_received_priority' ? 1 : 0 }));
    return suggestStopOrder(stops, pos ? { lat: pos.lat, lng: pos.lng } : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route, planKey, posKey]);
  const stopNo = new Map<string, number>();
  order?.stops.forEach((s, i) => s.paperIds.forEach((id) => stopNo.set(id, i + 1)));

  const selected = items.find((x) => x.p.id === sel) ?? null;
  const legend = (['nsh', 'gray'] as const).map((s) => ({ color: PIN_COLORS[s], label: s === 'nsh' ? 'Eligible now' : 'Not eligible now', count: items.filter((x) => x.state === s).length }));

  if (!open.length) return <Empty>No papers to serve on this device. Sync while online to load your assignments.</Empty>;

  return (
    <div className="stack field-map">
      <div className="row between wrap gap">
        <h1>Map</h1>
        <div className="row gap wrap small">
          <label className="row gap-sm">
            <input type="checkbox" checked={locate} onChange={(e) => setLocate(e.target.checked)} /> My location
          </label>
          <label className="row gap-sm">
            <input type="checkbox" checked={route} onChange={(e) => setRoute(e.target.checked)} /> Stop order
          </label>
        </div>
      </div>
      {!online && <Notice tone="warn">Offline — pins and rules still work; map imagery may not load.</Notice>}
      {locate && err && <Notice tone="warn">{err}</Notice>}
      <SlippyMap
        height="58vh"
        fitKey={`${located.length}`}
        ariaLabel="My papers map"
        me={locate ? pos : null}
        markers={located.map((x) => ({
          id: x.p.id,
          lat: x.pin!.lat,
          lng: x.pin!.lng,
          color: PIN_COLORS[x.state],
          label: stopNo.has(x.p.id) ? String(stopNo.get(x.p.id)) : undefined,
          title: `${x.p.civilosNumber} — ${x.p.defendant.full_name}, ${x.p.address.line1}. ${x.eligibleNow ? 'Eligible now' : 'Not eligible now'}`,
          diamond: true,
          selected: x.p.id === sel,
        }))}
        route={order && order.stops.length ? [...(pos && locate ? [pos] : []), ...order.stops.map((s) => ({ lat: s.lat, lng: s.lng }))] : undefined}
        onMarkerClick={setSel}
      />
      <PinLegend items={legend} />
      {selected && (
        <div className="map-popup" aria-live="polite">
          <div className="row between">
            <strong className="mono">{selected.p.civilosNumber}</strong>
            {stopNo.has(selected.p.id) && <Badge tone="info">Stop {stopNo.get(selected.p.id)}</Badge>}
          </div>
          <div>
            <strong>{selected.p.defendant.full_name}</strong> · {selected.p.evaluation.paperType.name}
          </div>
          <div className="muted small">{[selected.p.address.line1, selected.p.address.line2, selected.p.address.city].filter(Boolean).join(', ')}</div>
          <div className="small">{selected.eligibleNow ? PIN_LABELS.nsh : `Not eligible now${selected.ev?.attemptNow?.blockers?.[0] ? ` — ${selected.ev.attemptNow.blockers[0].message}` : ''}`}</div>
          <div className="row gap">
            <button className="btn primary" onClick={() => navigate(`/field/paper/${selected.p.id}`)}>
              Open
            </button>
            <a className="btn" href={navigateUrl(selected.p)} target="_blank" rel="noreferrer">
              Navigate
            </a>
            <button className="btn ghost" onClick={() => setSel(null)}>
              Close
            </button>
          </div>
        </div>
      )}
      {order && order.stops.length > 0 && (
        <div className="stack sm">
          <h2>
            Suggested order · {order.stops.length} stop(s) · {order.totalMiles} mi straight-line
          </h2>
          <ol className="stop-list">
            {order.stops.map((s, i) => {
              const ps = s.paperIds.map((id) => items.find((x) => x.p.id === id)!);
              return (
                <li key={s.addressKey}>
                  <button className={s.paperIds.includes(sel ?? '') ? 'active' : ''} onClick={() => setSel(s.paperIds[0]!)}>
                    <span className="stop-num" style={{ background: PIN_COLORS.nsh }}>
                      {i + 1}
                    </span>
                    <span className="grow">
                      <span>{ps[0]!.p.address.line1}</span>
                      <span className="muted small"> · {ps.map((x) => x.p.defendant.full_name).join(', ')}</span>
                      <span className="muted small"> · {s.legMiles} mi</span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
          <p className="small muted">{pos ? 'Nearest-next from your location.' : 'Starting from your most urgent paper — turn on My location for a better order.'} Only papers eligible right now are included.</p>
        </div>
      )}
      {missing.length > 0 && (
        <Notice tone="warn">
          {missing.length} paper(s) have no map location yet: {missing.map((x) => x.p.civilosNumber).join(', ')}. Open the paper and use Navigate, or set the pin when you arrive.
        </Notice>
      )}
    </div>
  );
}
